/**
 * Who is the host, who may moderate, and who may share.
 *
 * The local participant used to be hardcoded `role: 'host'`, so every user saw
 * "Host" under their own name and every `isModerator` gate was true for
 * everyone. Roles now come from the signaling server, which is also the only
 * place they can be trusted: the UI gate is cosmetic, the channel rejects
 * moderator events from non-moderators regardless of what the client believes.
 *
 * `presenter` is a capability rather than a role — host and co-host always have
 * it, an attendee has to be granted it, and without it sharing (screen,
 * whiteboard, document, video) is not offered.
 *
 * Annotation access is a separate grant (`annotators`): presenting a document
 * does not let anyone draw on one somebody else presents.
 *
 * Roles are session state: they are seeded from the meeting's durable host and
 * then live in the room until it ends, so a host transfer does not rewrite the
 * scheduled meeting's owner.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import type { PhoenixEventHandler } from './usePhoenixChannel'
import { vclog } from './vc-log'
import { parseShareBlocked, type BlockedShare } from '../services/share-only-roster'
import { readActor, type Actor } from '../services/moderator-notices'

export type RoomRole = 'host' | 'cohost' | 'attendee'

export type RoomRoles = {
  /** peerId → role. Anyone absent is an attendee. */
  byPeer: Record<string, RoomRole>
  /** peerIds explicitly granted presenter (moderators are implicit). */
  presenters: string[]
  /** peerIds granted annotation access (moderators are implicit). */
  annotators: string[]
  /** This client's role. */
  myRole: RoomRole
  /**
   * The room has sent its role list at least once, so `myRole` is the room's
   * answer rather than the "attendee" everyone reads as before it arrives.
   * Not the same as "my id is in `byPeer`": the server lists only hosts and
   * co-hosts there, so a plain attendee never appears in it.
   */
  heard: boolean
  /** Host or co-host — may run moderator actions. */
  isModerator: boolean
  /** May share screen / whiteboard / documents / video. */
  canPresent: boolean
  /** This client holds an annotation grant. Moderators draw without one. */
  isAnnotator: boolean
  roleOf: (peerId: string | undefined) => RoomRole
  canPeerPresent: (peerId: string | undefined) => boolean
  /** Holds an annotation grant (moderators are not listed; they need none). */
  hasAnnotatorGrant: (peerId: string | undefined) => boolean
  /**
   * The role in the UI's vocabulary, which is wider than the room's: an
   * attendee holding a presenter grant surfaces as "presenter" so the existing
   * permission matrix (screen-share: host | moderator | presenter) grants
   * sharing without a second, parallel check.
   */
  participantRole: (peerId: string | undefined) => 'host' | 'moderator' | 'presenter' | 'attendee'
  /**
   * Screen-share pages a moderator removed, kept out of the meeting until one
   * is allowed back (decision 11). Rides on every roles update; empty in every
   * meeting nobody removed one from, and from a server that predates it.
   */
  shareBlocked: BlockedShare[]
  /**
   * The moderator whose command produced the roles as they stand — set with
   * the same update, so a notice reading a role change can name them ("Asha
   * made you a co-host"). Null when nobody did: a join, a departure, a host
   * leaving and the room handing the role on, or an older server.
   */
  changedBy: Actor | null
}

type Args = {
  peerId: string | null
  subscribe: (event: string, handler: PhoenixEventHandler) => () => void
  /** Sends a channel event; used once on mount to pull the current roles. */
  sendEvent: (event: string, payload: Record<string, unknown>) => void
  channelJoined: boolean
}

function parseRoles(payload: Record<string, unknown>): {
  byPeer: Record<string, RoomRole>
  presenters: string[]
  annotators: string[]
  shareBlocked: BlockedShare[]
} {
  const raw = (payload.roles ?? {}) as Record<string, unknown>
  const byPeer: Record<string, RoomRole> = {}
  for (const [id, role] of Object.entries(raw)) {
    if (role === 'host' || role === 'cohost' || role === 'attendee') byPeer[id] = role
  }
  const ids = (list: unknown): string[] =>
    Array.isArray(list) ? list.filter((p): p is string => typeof p === 'string') : []
  return {
    byPeer,
    presenters: ids(payload.presenters),
    // Absent from a server that predates annotation access: nobody holds one.
    annotators: ids(payload.annotators),
    shareBlocked: parseShareBlocked(payload.share_blocked),
  }
}

/** Stable empty list, so a meeting nobody removed a share page from never re-renders for it. */
const NO_BLOCKED: BlockedShare[] = []

const sameBlocked = (a: BlockedShare[], b: BlockedShare[]) =>
  a.length === b.length && a.every((x, i) => x.id === b[i].id && x.name === b[i].name)

export function useRoomRoles({ peerId, subscribe, sendEvent, channelJoined }: Args): RoomRoles {
  const [byPeer, setByPeer] = useState<Record<string, RoomRole>>({})
  const [presenters, setPresenters] = useState<string[]>([])
  const [annotators, setAnnotators] = useState<string[]>([])
  const [shareBlocked, setShareBlocked] = useState<BlockedShare[]>(NO_BLOCKED)
  const [heard, setHeard] = useState(false)
  const [changedBy, setChangedBy] = useState<Actor | null>(null)

  useEffect(() => {
    const off = subscribe('roles:updated', (payload) => {
      setHeard(true)
      const next = parseRoles(payload)
      vclog('presence', 'roles updated', {
        host: Object.entries(next.byPeer).find(([, r]) => r === 'host')?.[0],
        presenters: next.presenters.length,
      })
      setByPeer(next.byPeer)
      setPresenters(next.presenters)
      setAnnotators(next.annotators)
      setShareBlocked((prev) => (sameBlocked(prev, next.shareBlocked) ? prev : next.shareBlocked))
      setChangedBy(readActor(payload))
    })
    return off
  }, [subscribe])

  // The join-time broadcast can land before this component subscribes, so ask
  // once the channel is up rather than waiting for the next role change.
  useEffect(() => {
    if (channelJoined) sendEvent('roles:get', {})
  }, [channelJoined, sendEvent])

  const roleOf = useCallback(
    (id: string | undefined): RoomRole => (id ? byPeer[id] ?? 'attendee' : 'attendee'),
    [byPeer],
  )

  const canPeerPresent = useCallback(
    (id: string | undefined): boolean => {
      if (!id) return false
      const role = byPeer[id] ?? 'attendee'
      return role === 'host' || role === 'cohost' || presenters.includes(id)
    },
    [byPeer, presenters],
  )

  const hasAnnotatorGrant = useCallback(
    (id: string | undefined): boolean => !!id && annotators.includes(id),
    [annotators],
  )

  const participantRole = useCallback(
    (id: string | undefined): 'host' | 'moderator' | 'presenter' | 'attendee' => {
      const role = roleOf(id)
      if (role === 'host') return 'host'
      if (role === 'cohost') return 'moderator'
      return canPeerPresent(id) ? 'presenter' : 'attendee'
    },
    [roleOf, canPeerPresent],
  )

  const myRole = roleOf(peerId ?? undefined)

  return useMemo(
    () => ({
      byPeer,
      presenters,
      myRole,
      heard,
      isModerator: myRole === 'host' || myRole === 'cohost',
      canPresent: canPeerPresent(peerId ?? undefined),
      annotators,
      isAnnotator: hasAnnotatorGrant(peerId ?? undefined),
      roleOf,
      canPeerPresent,
      hasAnnotatorGrant,
      participantRole,
      shareBlocked,
      changedBy,
    }),
    [byPeer, presenters, annotators, myRole, heard, canPeerPresent, hasAnnotatorGrant, peerId, roleOf, participantRole, shareBlocked, changedBy],
  )
}
